import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { DEFAULT_PERMISSION_GROUP_NAME_KEYS, fromRxValue, GROUP_ALIAS_MAP } from "@app/_helpers/utils";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { UserSettings, UserSettingsQuery } from "@timeghost/web-api";
import { xor } from "lodash-es";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { firstBy } from "thenby";

@UntilDestroy()
@Component({
	selector: "tg-groups-filter-popover",
	templateUrl: "./groups-filter-popover.component.html",
	styleUrls: ["./groups-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class GroupsFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}

	search = new UntypedFormControl("", [Validators.required, Validators.minLength(2)]);
	private _selectedItems = new BehaviorSubject<string[]>([]);
	readonly selectedItems$ = this._selectedItems.asObservable().pipe(distinctUntilChanged());
	filteredGroups: UserSettings["workspace"]["groups"] = [];

	get selectedItems() {
		return this._selectedItems.getValue();
	}

	@Input() excludeSystemGroups = false;

	@Input()
	set selectedItems(val: string[]) {
		const prev = this.selectedItems;
		this._selectedItems.next(val);

		if (xor(val, prev).length > 0) this.selectedItemsChange.emit(val);
	}

	@Output() selectedItemsChange = new EventEmitter(true);

	items$filtered = fromRxValue(
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.value)),
			this.userSettingsQuery.select(),
			this.selectedItems$,
		]).pipe(
			map(([q, user, selected]: [string, UserSettings, string[]]) => {
				const allGroups = this.excludeSystemGroups
					? (user.workspace.groups || []).filter((x) => !DEFAULT_PERMISSION_GROUP_NAME_KEYS[x.id])
					: user.workspace.groups || [];
				const filteredGroups = (() => {
					if (this.search.invalid) return allGroups;
					return allGroups.filter((x) => {
						const tName = GROUP_ALIAS_MAP[x.id];
						return (
							(tName ? this.translate.instant(tName) : x.name).toLowerCase().indexOf(q.toLowerCase()) !==
							-1
						);
					});
				})()
					.map((x) => {
						const tName = x.id && GROUP_ALIAS_MAP[x.id];
						return {
							...x,
							name: tName || x.name,
							useTranslate: !!tName,
							selected: selected.findIndex((u) => u === x.id) !== -1,
						};
					})
					.sort(firstBy("selected", "desc").thenBy("name", "asc"));
				this.filteredGroups = filteredGroups;
				return filteredGroups;
			})
		)
	);

	toggle() {
		this._open ? this.close() : this.open();
	}

	@Output() itemsClose = new EventEmitter(true);

	ngOnInit(): void {
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}

	selectItem(id: string) {
		if (this.selectedItems.findIndex((x) => x === id) !== -1)
			this.selectedItems = this.selectedItems.filter((x) => x !== id);
		else this.selectedItems = [...this.selectedItems, id];
	}

	selectAll() {
		this.selectedItems = this.items$filtered.value.map((group) => group.id).filter((id) => !!id);
	}

	selectNone() {
		this.selectedItems = [];
	}

	close() {
		super.close(this.selectedItems);
	}
}
